import { getRequestInstance, type RequestOptions } from '@@/plugin-request/request';
import { type RequestConfig } from '@umijs/max';
import { notification } from 'antd';
import { message as messageUtil } from '@/utils/antdInstance';
import { clearToken, getToken, refreshTokenFun } from '@/utils/util';

enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

const LOGIN_PATH = '/user/login';
const LOGIN_API_PATH = '/api/v1/auth/login';
const REFRESH_API_PATH = '/api/v1/auth/token/refresh';
const RETRY_AFTER_REFRESH_HEADER = 'X-Retry-After-Refresh';
// 在此集中维护无需携带菜单路径的接口。
const MENU_PATH_EXCLUDED_API_PATHS = ['/agent-api/v1/agent/conversations/nav', '/agent-api/v1/agent/conversations/facets'];

let refreshingTokenPromise: Promise<boolean> | null = null;

const redirectToLogin = () => {
  clearToken();
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.href = LOGIN_PATH;
  }
};

const shouldSkipRefresh = (url?: string) => {
  if (!url) return false;
  return url.includes(LOGIN_API_PATH) || url.includes(REFRESH_API_PATH);
};

const shouldExcludeMenuPath = (url?: string) => {
  if (!url) return false;

  const pathname = new URL(url, window.location.origin).pathname;
  return MENU_PATH_EXCLUDED_API_PATHS.includes(pathname);
};

const hasRetriedAfterRefresh = (config?: RequestOptions) => {
  const headers = (config?.headers || {}) as Record<string, any>;
  return headers[RETRY_AFTER_REFRESH_HEADER] === '1';
};

const ensureTokenRefreshed = async () => {
  if (!refreshingTokenPromise) {
    refreshingTokenPromise = refreshTokenFun().finally(() => {
      refreshingTokenPromise = null;
    });
  }

  return refreshingTokenPromise;
};

export const errorConfig: RequestConfig = {
  errorConfig: {
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } = res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error;
      }
    },
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              break;
            case ErrorShowType.WARN_MESSAGE:
              messageUtil.warning(errorMessage || '');
              break;
            case ErrorShowType.ERROR_MESSAGE:
              messageUtil.error(errorMessage || '');
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              break;
            default:
              messageUtil.error(errorMessage || '');
          }
        }
      } else if (error.response) {
        if (error.response.status === 401) {
          return;
        }
        messageUtil.error(error.response.data?.msg || error.response.data?.message || `Response status:${error.response.status}`);
      } else if (error.request) {
        messageUtil.error('None response! Please retry.');
      } else {
        messageUtil.error('Request error, please retry.');
      }
    },
  },

  requestInterceptors: [
    (config: RequestOptions) => {
      const url = config.url;
      if (!url?.includes('/login')) {
        const token = getToken();
        if (token) {
          const headers: any = {
            ...config.headers,
            Authorization: `Bearer ${token}`,
          };
          if (!shouldExcludeMenuPath(url)) {
            headers['X-Menu-Path'] = window.location.pathname;
          }
          config.headers = headers;
        }
      }

      const finalConfig = { ...config, url };
      if (finalConfig.timeout === undefined) {
        finalConfig.timeout = 1000 * 60 * 3;
      }
      return finalConfig;
    },
  ],

  responseInterceptors: [
    [
      (response: any) => response,
      async (error: any): Promise<any> => {
        const originalConfig: RequestOptions | undefined = error?.config;
        const status = error?.response?.status;
        const requestUrl = originalConfig?.url;

        if (status !== 401) {
          throw error;
        }

        if (!originalConfig) {
          throw error;
        }

        if (shouldSkipRefresh(requestUrl) || hasRetriedAfterRefresh(originalConfig)) {
          redirectToLogin();
          throw error;
        }

        if (!getToken()) {
          redirectToLogin();
          throw error;
        }

        const refreshed = await ensureTokenRefreshed();
        if (!refreshed) {
          redirectToLogin();
          throw error;
        }

        const requestInstance = getRequestInstance();
        return requestInstance.request({
          ...originalConfig,
          headers: {
            ...(originalConfig.headers || {}),
            [RETRY_AFTER_REFRESH_HEADER]: '1',
          },
        });
      },
    ],
  ],
};
